import { parseOverlayBagKey } from "src/overlayAttributes/personOverlayAttributeKeys";

import { resolveOverlayDisplayLabel } from "./attributeLabels";

export const GRADE_SOURCES = [
  {
    value: "personAttribute",
    label: "Person attribute",
    description: "The grade is a field on the person.",
  },
  {
    value: "positionAttribute",
    label: "Position attribute",
    description: "The grade is a field on the position.",
  },
  {
    value: "personTag",
    label: "Person tag",
    description: "The grade is a tag on the person.",
  },
  {
    value: "positionTag",
    label: "Position tag",
    description: "The grade is a tag on the position.",
  },
] as const;

export type GradeSource = (typeof GRADE_SOURCES)[number]["value"];

export type GradeFieldOption = { value: string; label: string };

export type GradeCatalogRow = {
  key: string;
  label?: string | null;
  entityTypes: readonly string[];
  settingsExcluded?: boolean;
  internal?: boolean;
  hidden?: boolean;
};

/** Person and position columns whose distinct values are not in the attribute bag. */
const SCALAR_VALUE_ATTRIBUTE_TYPE: Record<string, string> = {
  country: "country",
  city: "city",
  state: "state",
  address: "address",
  company: "company",
  timezone: "timezone",
  bands: "bands",
  directWorkforce: "directWorkforce",
  fte: "fte",
  "position.jobTitle": "jobTitle",
  "position.fte": "fte",
  "position.name": "positionName",
  "position.location": "positionLocation",
};

export type GradeTagTypeRow = {
  id: string;
  name?: string | null;
  allowPersonToHaveTag?: boolean | null;
  allowPositionToHaveTag?: boolean | null;
  isActive?: boolean | null;
};

export const GRADE_COMPRESSION_DEFINITION =
  "The number of reporting lines where a person and the person they report to have the same grade. The measure is the same in every workspace. A workspace chooses one field, which may be a person attribute, a position attribute, a person tag, or a position tag.";

const pushUnique = (
  options: GradeFieldOption[],
  value: string,
  label: string
) => {
  if (options.some((option) => option.value === value)) {
    return;
  }
  options.push({ value, label });
};

export const gradeAttributeValueArgs = (
  source: GradeSource,
  field: string
): { attributeType: string; subtype: string } | null => {
  const trimmed = field.trim();
  if (
    !trimmed ||
    (source !== "personAttribute" && source !== "positionAttribute")
  ) {
    return null;
  }
  const parsed = parseOverlayBagKey(trimmed);
  if (parsed) {
    return {
      attributeType: parsed.attributeType,
      subtype: parsed.subtype,
    };
  }
  const scalarType = SCALAR_VALUE_ATTRIBUTE_TYPE[trimmed];
  if (scalarType) {
    return { attributeType: scalarType, subtype: "" };
  }
  return {
    attributeType: source === "personAttribute" ? "person" : "position",
    subtype: trimmed,
  };
};

export const gradeFieldOptions = (
  source: GradeSource,
  catalog: readonly GradeCatalogRow[],
  tagTypes: readonly GradeTagTypeRow[],
  labels: Readonly<Record<string, string | undefined>> = {}
): GradeFieldOption[] => {
  if (source === "personAttribute" || source === "positionAttribute") {
    const entityType = source === "personAttribute" ? "person" : "position";
    const options: GradeFieldOption[] = [];
    for (const row of catalog) {
      if (
        !row.key ||
        row.hidden ||
        row.settingsExcluded ||
        row.internal ||
        !row.entityTypes.includes(entityType)
      ) {
        continue;
      }
      pushUnique(
        options,
        row.key,
        resolveOverlayDisplayLabel({
          key: row.key,
          catalogLabel: row.label,
          labelsByKey: labels,
        })
      );
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  const options: GradeFieldOption[] = [];
  for (const tagType of tagTypes) {
    if (tagType.isActive === false) {
      continue;
    }
    const allowed =
      source === "personTag"
        ? tagType.allowPersonToHaveTag
        : tagType.allowPositionToHaveTag;
    if (!allowed || !tagType.id) {
      continue;
    }
    pushUnique(options, tagType.id, tagType.name?.trim() || tagType.id);
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export const withCurrentGradeField = (
  options: readonly GradeFieldOption[],
  field: string
): GradeFieldOption[] => {
  const trimmed = field.trim();
  if (!trimmed || options.some((option) => option.value === trimmed)) {
    return [...options];
  }
  return [{ value: trimmed, label: trimmed }, ...options];
};

export const gradeFieldEmptyHelp = (source: GradeSource): string => {
  if (source === "personTag") {
    return "This workspace has no tag types that apply to a person.";
  }
  if (source === "positionTag") {
    return "This workspace has no tag types that apply to a position.";
  }
  return "This workspace has no fields for this source.";
};

export const gradeValueOptions = (
  values: readonly string[]
): GradeFieldOption[] => {
  const options: GradeFieldOption[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed) {
      pushUnique(options, trimmed, trimmed);
    }
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export type GradeRank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export const MAX_GRADE_RANK = 10;

export const GRADE_RANKS: { value: GradeRank; label: string }[] = [
  { value: 10, label: "Rank 10" },
  { value: 9, label: "Rank 9" },
  { value: 8, label: "Rank 8" },
  { value: 7, label: "Rank 7" },
  { value: 6, label: "Rank 6" },
  { value: 5, label: "Rank 5" },
  { value: 4, label: "Rank 4" },
  { value: 3, label: "Rank 3" },
  { value: 2, label: "Rank 2" },
  { value: 1, label: "Rank 1" },
];

export const GRADE_COMPRESSION_SHOWS = [
  "Grade compression is the top card. It counts people who have the same grade as the person they report to. The number opens those people and their grade. A count of zero has no link. This card needs a source and a field.",
  "The first grade in the list is the most senior. Six grades are ranks 6 down to 1. A value that is not in the list is Other. A target share is optional. Leave one share blank and it becomes what is left to reach 100.",
] as const;

export const GRADE_COMPRESSION_HELP = {
  source:
    "Where the grade is stored. A person attribute or person tag is read on the person. A position attribute or position tag is read on the seat.",
  field:
    "A visible attribute from Org Design settings, or a tag type. Hidden attributes are left out. The names can differ by workspace. The comparison does not.",
  value: "One grade from that field. Each line is a single grade.",
  rank: "The order of the grades is the rank. Drag a row, or move it up or down. The first grade is the most senior. Six grades are ranks 6 down to 1. Rank 1 is the least senior.",
  targetShare:
    "Optional. The percent of graded people this grade should be, from 0 to 100. Leave one share blank and it becomes what is left to reach 100. The percents must not add up to more than 100.",
} as const;

export type GradeOrderRow = {
  value: string;
  rank: GradeRank | null;
  targetShare: string;
};

export type GradeCompressionDraft = {
  source: GradeSource;
  field: string;
  order: GradeOrderRow[];
};

export type PersistedGradeCompression = {
  source: GradeSource;
  field: string;
  order: { value: string; rank: GradeRank; targetShare?: number }[];
};

const isRank = (value: unknown): value is GradeRank =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= 1 &&
  value <= MAX_GRADE_RANK;

const isSource = (value: string): value is GradeSource =>
  GRADE_SOURCES.some((source) => source.value === value);

const blankGradeRow = (): GradeOrderRow => ({
  value: "",
  rank: null,
  targetShare: "",
});

const filledGradeIndexes = (order: readonly GradeOrderRow[]): number[] =>
  order.flatMap((row, index) => (row.value.trim() ? [index] : []));

/** First filled grade is the most senior. Six grades are ranks 6 down to 1. */
export const listedGradeRank = (
  order: readonly GradeOrderRow[],
  index: number
): GradeRank | null => {
  const places = filledGradeIndexes(order);
  const place = places.indexOf(index);
  if (place < 0) {
    return null;
  }
  const rank = places.length - place;
  return isRank(rank) ? rank : null;
};

export const resolvedGradeRank = (
  order: readonly GradeOrderRow[],
  index: number
): GradeRank | null => listedGradeRank(order, index);

/** When exactly one filled grade has no share, that share is what is left of 100. */
export const remainderTargetShare = (
  order: readonly GradeOrderRow[],
  index: number
): string | null => {
  const places = filledGradeIndexes(order);
  if (places.length < 2 || !places.includes(index)) {
    return null;
  }
  const blanks = places.filter(
    (place) => order[place]?.targetShare.trim() === ""
  );
  if (blanks.length !== 1 || blanks[0] !== index) {
    return null;
  }
  let sum = 0;
  for (const place of places) {
    if (place === index) {
      continue;
    }
    const share = Number(order[place]?.targetShare.trim());
    if (!Number.isFinite(share)) {
      return null;
    }
    sum += share;
  }
  const remainder = Math.round((100 - sum) * 100) / 100;
  if (remainder < 0 || remainder > 100) {
    return null;
  }
  return String(remainder);
};

const ranksFollowTheList = (order: readonly GradeOrderRow[]): boolean => {
  const places = filledGradeIndexes(order);
  return (
    places.length > 0 &&
    places.every((index, place) => order[index]?.rank === places.length - place)
  );
};

export const addGradeRow = (
  draft: GradeCompressionDraft
): GradeCompressionDraft => ({
  ...draft,
  order: [...draft.order, blankGradeRow()],
});

export const moveGradeRow = (
  draft: GradeCompressionDraft,
  from: number,
  to: number
): GradeCompressionDraft => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= draft.order.length ||
    to >= draft.order.length
  ) {
    return draft;
  }
  const order = [...draft.order];
  const [row] = order.splice(from, 1);
  if (!row) {
    return draft;
  }
  order.splice(to, 0, row);
  return { ...draft, order };
};

export const removeGradeRow = (
  draft: GradeCompressionDraft,
  index: number
): GradeCompressionDraft => ({
  ...draft,
  order: draft.order.filter((_, rowIndex) => rowIndex !== index),
});

export const updateGradeRow = (
  draft: GradeCompressionDraft,
  index: number,
  patch: Partial<Pick<GradeOrderRow, "value" | "rank" | "targetShare">>
): GradeCompressionDraft => {
  const current = draft.order[index];
  if (!current) {
    return draft;
  }
  const order = draft.order.map((row, rowIndex) =>
    rowIndex === index ? { ...current, ...patch } : row
  );
  return { ...draft, order };
};

export const optionsForRow = (
  draft: GradeCompressionDraft,
  index: number,
  options: readonly GradeFieldOption[]
): GradeFieldOption[] => {
  const taken = new Set(
    draft.order
      .filter((_, rowIndex) => rowIndex !== index)
      .map((row) => row.value.trim())
  );
  const available = options.filter((option) => !taken.has(option.value));
  return withCurrentGradeField(available, draft.order[index]?.value ?? "");
};

export const emptyGradeCompressionDraft = (): GradeCompressionDraft => ({
  source: "personAttribute",
  field: "",
  order: [],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const gradeCompressionFromSaved = (
  raw: unknown
): GradeCompressionDraft => {
  if (!isRecord(raw) || typeof raw["field"] !== "string") {
    return emptyGradeCompressionDraft();
  }
  const source =
    typeof raw["source"] === "string" && isSource(raw["source"])
      ? raw["source"]
      : "personAttribute";
  const order = Array.isArray(raw["order"])
    ? raw["order"].flatMap((row) => {
        if (!isRecord(row) || typeof row["value"] !== "string") {
          return [];
        }
        if (!isRank(row["rank"])) {
          return [];
        }
        const targetShare =
          typeof row["targetShare"] === "number"
            ? String(row["targetShare"])
            : "";
        return [{ value: row["value"], rank: row["rank"], targetShare }];
      })
    : [];
  return {
    source,
    field: raw["field"],
    order: ranksFollowTheList(order)
      ? order.map((row) => ({ ...row, rank: null }))
      : order,
  };
};

export const gradeCompressionSaveError = (
  draft: GradeCompressionDraft
): string | null => {
  if (!draft.field.trim()) {
    return "Grade compression needs a field.";
  }
  const seen = new Set<string>();
  let targetTotal = 0;
  for (const [index, row] of draft.order.entries()) {
    const value = row.value.trim();
    if (!value) {
      continue;
    }
    const rawShare = row.targetShare.trim();
    if (rawShare !== "") {
      const share = Number(rawShare);
      if (!Number.isFinite(share) || share < 0 || share > 100) {
        return "Grade compression target share must be a number from 0 to 100.";
      }
      targetTotal += share;
    }
    if (seen.has(value)) {
      return "Grade compression values must be unique.";
    }
    seen.add(value);
    if (resolvedGradeRank(draft.order, index) === null) {
      return "Grade compression can rank up to 10 grades.";
    }
  }
  if (Math.round(targetTotal * 100) / 100 > 100) {
    return "Grade compression target shares add up to more than 100.";
  }
  return null;
};

/** Switch off saves without a field. An invalid form is left unsaved. */
export const gradeCompressionSavePlan = (
  enabled: boolean,
  dirty: boolean,
  draft: GradeCompressionDraft
): { error: string | null; persist: boolean } => {
  if (!dirty) {
    return { error: null, persist: false };
  }
  const error = gradeCompressionSaveError(draft);
  if (!enabled) {
    return { error: null, persist: error === null };
  }
  return { error, persist: error === null };
};

export const toPersistedGradeCompression = (
  draft: GradeCompressionDraft
): PersistedGradeCompression => ({
  source: draft.source,
  field: draft.field.trim(),
  order: draft.order
    .flatMap((row, index) => {
      const value = row.value.trim();
      const rank = resolvedGradeRank(draft.order, index);
      if (!value || rank === null) {
        return [];
      }
      const next: PersistedGradeCompression["order"][number] = {
        value,
        rank,
      };
      const shareText =
        row.targetShare.trim() ||
        remainderTargetShare(draft.order, index) ||
        "";
      if (shareText !== "") {
        next.targetShare = Number(shareText);
      }
      return [next];
    })
    .sort((a, b) => b.rank - a.rank),
});
